import { rivalMovesWeek } from "./rivalmoves";
import { followUpName } from "./names";
import { pitchesWeek } from "./pitches";
import { C, cardRules, components, licences, type PartId } from "@bd/content";
import { priceIndex, WEEKS_PER_YEAR, yearOf } from "./calendar";
import { perkFx } from "./perks";
import type { Rng } from "./rng";
import type { Card, Company, Design, GameState, MetaState, Product, Project } from "./state";
import { isUnlocked } from "./unlocks";
import { clamp, log, money, newId, rng } from "./util";
import { dexp, dpow } from "./dmath";

/**
 * Live products: a good game becomes a product line. Expansions sell to the base game's owners, deluxe editions
 * revive old hits, spin-offs reuse a theme's fans, card-game lines live or die by their tournament meta,
 * RPG lines grow a community, and licences tie a game to an in-world film, show or book.
 */

export type LineKind = "expansion" | "edition" | "spinoff" | "card_set" | "rpg_supplement";

export const LINE_LABEL: Record<LineKind, string> = {
  expansion: "Expansion",
  edition: "Deluxe edition",
  spinoff: "Spin-off",
  card_set: "Next card set",
  rpg_supplement: "Supplement",
};

/** Share of a full project's work each kind needs. */
export const LINE_WORK: Record<Project["kind"], number> = { base: 1, spinoff: 1, license: 1, expansion: 0.5, edition: 0.35, card_set: 0.45, rpg_supplement: 0.45 };

const BASE_KINDS = new Set<Product["kind"]>(["base", "spinoff", "license"]);

/** The core product a line hangs off (an expansion's base game, a card set's core set). */
export function lineRoot(co: Company, prod: Product): Product {
  let p = prod;
  for (let i = 0; i < 8 && p.parentId; i++) {
    const up = co.products.find((x) => x.id === p.parentId);
    if (!up) break;
    p = up;
  }
  return p;
}

export function children(co: Company, root: Product, kind?: Product["kind"]): Product[] {
  return co.products.filter((p) => p.parentId === root.id && (!kind || p.kind === kind));
}

export interface LineOption {
  kind: LineKind;
  ok: boolean;
  why: string;
}

/** What could follow this product, and why not when it can't. */
export function lineOptions(state: GameState, co: Company, prod: Product): LineOption[] {
  const year = yearOf(state.week);
  const age = (state.week - prod.releaseWeek) / WEEKS_PER_YEAR;
  const out: LineOption[] = [];
  if (!prod.launched || prod.retired) return out;
  const fmt = prod.design.format;
  const busy = (kind: Product["kind"]) => co.projects.some((p) => p.parentId === prod.id && p.kind === kind);
  if (fmt === "ccg") {
    if (prod.kind === "base") out.push({ kind: "card_set", ok: !busy("card_set"), why: busy("card_set") ? "A set is already in the works." : "New cards for the players who own the core set." });
  } else if (fmt === "rpg_book") {
    if (prod.kind === "base") out.push({ kind: "rpg_supplement", ok: !busy("rpg_supplement"), why: busy("rpg_supplement") ? "A book is already in the works." : "Adventures and setting books for the core rulebook's players." });
  } else if (BASE_KINDS.has(prod.kind)) {
    const n = children(co, prod, "expansion").length;
    const exp = prod.score < 6 ? "Only games scoring 6 or more get expansions." : busy("expansion") ? "One is already in the works." : n >= 3 ? `Expansion ${n + 1}: fans are getting tired of more of the same.` : "Sells to a share of the base game's owners; more if people keep replaying it.";
    out.push({ kind: "expansion", ok: prod.score >= 6 && !busy("expansion"), why: exp });
  }
  if (BASE_KINDS.has(prod.kind)) {
    const recentEdition = children(co, prod, "edition").some((e) => state.week - e.releaseWeek < 10 * WEEKS_PER_YEAR) || busy("edition");
    // one deluxe edition a year across your whole catalogue: it's an event, not a habit
    const editionThisYear = co.products.some((x) => x.kind === "edition" && x.launched && state.week - x.releaseWeek < WEEKS_PER_YEAR) || co.projects.some((x) => x.kind === "edition");
    const ok = age >= 5 && prod.score >= 7 && !recentEdition && !editionThisYear;
    out.push({ kind: "edition", ok, why: ok ? `A ${Math.round(age) >= 25 ? "25th-anniversary" : "deluxe"} edition: better parts, higher price, old fans come back.` : prod.score < 7 ? "Only hits scoring 7 or more get deluxe editions." : age < 5 ? `Wait until it's 5 years old (${Math.max(1, Math.ceil(5 - age))} more).` : editionThisYear ? "One deluxe edition a year: you've had this year's." : "There was an edition recently." });
    const ok2 = prod.score >= 7;
    out.push({ kind: "spinoff", ok: ok2, why: ok2 ? "A new mechanic with the same theme: the fans follow you." : "Spin-offs need a game scoring 7 or more." });
  }
  void year;
  return out;
}

function bestPart(co: Company, part: PartId, current: string | undefined, week: number): string | undefined {
  const opts = components.filter((c) => c.part === part && c.year <= yearOf(week) && (isUnlocked(co, `c:${c.id}`) || (!c.research && c.year <= 1965)));
  const cur = components.find((c) => c.id === current);
  const better = opts.filter((c) => c.tier > (cur?.tier ?? 0) && !(c as { needsRoom?: string }).needsRoom).sort((a, b) => b.tier - a.tier)[0];
  return better?.id ?? current;
}

/** The design a follow-up starts from. The player can rename it (and pick the mechanic for a spin-off). */
export function lineDesign(state: GameState, co: Company, parent: Product, kind: LineKind): Design {
  const d: Design = JSON.parse(JSON.stringify(parent.design));
  const n = children(co, parent, kind).length + 1;
  const idx = priceIndex(state.week) / priceIndex(parent.releaseWeek);
  d.price = Math.round(d.price * idx * 2) / 2;
  if (kind === "expansion") {
    d.name = followUpName(co, parent.design.name, "expansions", n);
    // an expansion is new cards and tiles in a small box: no second board, dice or miniatures
    for (const part of Object.keys(d.parts) as PartId[]) {
      if (part === "board" || part === "dice" || part === "minis" || part === "insert" || part === "extras") d.parts[part] = `${part}_none`;
      if (part === "box") d.parts.box = "box_small";
    }
    d.price = Math.max(1, Math.round(d.price * 0.7 * 2) / 2);
  } else if (kind === "edition") {
    const age = Math.round((state.week - parent.releaseWeek) / WEEKS_PER_YEAR);
    d.name = `${parent.design.name}: ${age >= 25 ? `${age}th Anniversary Edition` : "Deluxe Edition"}`;
    for (const part of Object.keys(d.parts) as PartId[]) d.parts[part] = bestPart(co, part, d.parts[part], state.week);
    d.price = Math.round(d.price * 1.7 * 2) / 2;
  } else if (kind === "spinoff") {
    d.name = followUpName(co, parent.design.name, "spinoffs", n);
  } else if (kind === "card_set") {
    d.name = `${parent.design.name}: Set ${n + 1}`;
  } else if (kind === "rpg_supplement") {
    d.name = followUpName(co, parent.design.name, "supplements", n);
    d.price = Math.max(1, Math.round(d.price * 0.7 * 2) / 2);
  }
  return d;
}

// ------------------------------------------------------------------ card sets and the meta

export const ARCHETYPES = cardRules.archetypes.map((a) => a.id);

/** Validate and normalise the hand-designed headline cards (up to three). */
export function headlineCards(state: GameState, cards: Partial<Card>[] | undefined): Card[] {
  const out: Card[] = [];
  for (const c of (cards ?? []).slice(0, 3)) {
    const kw = C.keyword[c.keyword ?? ""];
    if (!kw) continue;
    out.push({
      id: newId(state, "card"), name: (c.name ?? "").trim().slice(0, 30) || `${kw.name} Card`, power: clamp(Math.round(c.power ?? 5), 1, 10),
      keyword: kw.id, archetype: kw.archetype, rarity: (c.power ?? 5) >= 8 ? "mythic" : "rare", answer: c.answer && C.archetype[c.answer] ? c.answer : undefined,
    });
  }
  return out;
}

/** Fill the rest of a set: the power budget spread over archetypes. Better design quality spreads it more evenly. */
export function fillSet(prod: Product, quality: number, r: Rng): Record<string, number> {
  const power: Record<string, number> = Object.fromEntries(ARCHETYPES.map((a) => [a, 0]));
  const budget = cardRules.powerBudget;
  const lumpy = 1.4 - quality; // 0.4 (careful designers) .. 1.4 (rushed)
  const weights = ARCHETYPES.map(() => dexp(r.normal(0, 0.45 * lumpy)));
  const sum = weights.reduce((a, b) => a + b, 0);
  ARCHETYPES.forEach((a, i) => (power[a] = Math.round((budget * weights[i]) / sum)));
  for (const c of prod.cards ?? []) {
    power[c.archetype] += c.power * 4; // a headline card is worth a few commons
    if (c.answer) power[c.answer] -= c.power * 3;
  }
  return power;
}

function legalSets(co: Company, root: Product): Product[] {
  return [root, ...children(co, root, "card_set")].filter((p) => p.launched && !p.rotated);
}

/** Thousands of tournament games, simplified: each archetype's strength is the card power behind it, and players drift to winning decks. */
export function runMeta(state: GameState, co: Company, root: Product, games = 1000): MetaState {
  const r = rng(state, `meta:${root.id}`);
  const strength: Record<string, number> = Object.fromEntries(ARCHETYPES.map((a) => [a, 1]));
  for (const set of legalSets(co, root)) {
    for (const [a, v] of Object.entries(set.setPower ?? {})) strength[a] = (strength[a] ?? 1) + v;
    for (const c of set.cards ?? []) if (c.banned) strength[c.archetype] -= c.power * 4;
  }
  const prev = root.meta;
  const pop: Record<string, number> = Object.fromEntries(ARCHETYPES.map((a) => [a, prev ? 0.5 + (prev.winRates[a] ?? 0.5) : 1]));
  const wins: Record<string, number> = Object.fromEntries(ARCHETYPES.map((a) => [a, 0]));
  const played: Record<string, number> = Object.fromEntries(ARCHETYPES.map((a) => [a, 0]));
  // cumulative popularity, so each pairing is two lookups rather than two weighted scans
  const cum: number[] = [];
  let tot = 0;
  for (const a of ARCHETYPES) cum.push((tot += pop[a]));
  const pickDeck = (): string => {
    const x = r.next() * tot;
    let i = 0;
    while (i < cum.length - 1 && x >= cum[i]) i++;
    return ARCHETYPES[i];
  };
  for (let g = 0; g < games; g++) {
    const a = pickDeck();
    const b = pickDeck();
    if (a === b) continue;
    const sa = Math.max(1, strength[a]);
    const sb = Math.max(1, strength[b]);
    const pA = 1 / (1 + dpow(sb / sa, 2.2));
    const winner = r.chance(pA) ? a : b;
    played[a]++;
    played[b]++;
    wins[winner]++;
  }
  const winRates: Record<string, number> = {};
  for (const a of ARCHETYPES) winRates[a] = played[a] ? wins[a] / played[a] : 0.5;
  const top = ARCHETYPES.reduce((best, a) => (winRates[a] > winRates[best] ? a : best), ARCHETYPES[0]);
  const dominance = winRates[top];
  let health = prev?.health ?? 1;
  // a solved meta (one deck wins far too often) drives players away; a healthy one slowly heals
  health = dominance > 0.6 ? health - (dominance - 0.6) * 0.8 : health + 0.03;
  health = clamp(health, 0.25, 1);
  const history = [...(prev?.history ?? []), { week: state.week, dominance, top }].slice(-24);
  root.meta = { lastRun: state.week, winRates, top, dominance, health, history };
  if (dominance > 0.62 && (!prev || prev.dominance <= 0.62)) log(state, "meta", `The ${root.design.name} meta is solved: ${C.archetype[top].name} decks win ${Math.round(dominance * 100)}% of games. Ban a card, print an answer, or rotate a set.`, co.id);
  return root.meta;
}

export function banCard(state: GameState, co: Company, cardId: string): string | null {
  for (const p of co.products) {
    const c = p.cards?.find((x) => x.id === cardId);
    if (!c) continue;
    if (c.banned) return "Already banned.";
    c.banned = true;
    co.reputation = clamp(co.reputation - 2, 0, 100);
    co.fans = Math.round(co.fans * 0.97); // owners of the card are angry
    const root = lineRoot(co, p);
    runMeta(state, co, root);
    log(state, "ban", `${c.name} is banned from tournaments. Its owners grumble, but the game is fairer.`, co.id);
    return null;
  }
  return "No such card.";
}

export function rotateOldest(state: GameState, co: Company, root: Product): string | null {
  const legal = legalSets(co, root).filter((p) => p.id !== root.id);
  if (legal.length < 3) return "Rotation needs at least three sets after the core set.";
  const oldest = legal.sort((a, b) => a.releaseWeek - b.releaseWeek)[0];
  oldest.rotated = true;
  co.fans = Math.round(co.fans * 0.98);
  runMeta(state, co, root);
  log(state, "rotate", `${oldest.design.name} rotates out of tournament play. New players find it easier to join.`, co.id);
  return null;
}

// ------------------------------------------------------------------ demand for line products

/** Copies a follow-up can still sell to the line's owners: the owner pool times a share that grows with replay intent. */
export function lineDemand(co: Company, prod: Product, weeksSince: number, sold = prod.sold): number | null {
  if (prod.kind !== "expansion" && prod.kind !== "card_set" && prod.kind !== "rpg_supplement") return null;
  const root = lineRoot(co, prod);
  const owners = root.sold;
  const replay = root.trueReplay;
  const siblings = children(co, root, prod.kind).filter((p) => p.launched && p.releaseWeek <= prod.releaseWeek && p.id !== prod.id).length;
  let share: number;
  let tau: number;
  if (prod.kind === "expansion") {
    share = 0.2 + 0.65 * replay;
    share /= 1 + 0.4 * Math.max(0, siblings + 1 - 3); // fatigue after the third
    tau = 14;
  } else if (prod.kind === "card_set") {
    share = (0.35 + 0.5 * replay) * (root.meta?.health ?? 1) * (1 + perkFx(co, "ccgSales"));
    if (prod.rotated) share *= 0.3;
    tau = 20;
  } else {
    share = (0.2 + 0.45 * replay) * (1 + (root.community ?? 0));
    share /= 1 + 0.15 * Math.max(0, siblings + 1 - 6);
    tau = 26;
  }
  const quality = dpow(clamp(prod.score / 7, 0.3, 1.4), 1.5);
  const potential = owners * share * quality;
  const left = Math.max(0, potential - sold);
  return (left / tau) * dexp(-Math.max(0, weeksSince - 8) / (tau * 4));
}

/** Demand multiplier for a whole line (card-game health, licence popularity). */
export function lineFactor(state: GameState, co: Company, prod: Product): number {
  let f = 1;
  if (prod.design.format === "ccg" && prod.kind === "base") f *= prod.meta?.health ?? 1;
  if (prod.licence && state.week < prod.licence.until) f *= prod.licence.flopped ? 0.7 : C.licence[prod.licence.id]?.boost ?? 1;
  return f;
}

// ------------------------------------------------------------------ launches and weekly upkeep

/** Called when a follow-up launches. */
export function onLineLaunch(state: GameState, co: Company, prod: Product, p: Project): void {
  const r = rng(state, `lines:${co.id}`);
  if (prod.design.format === "ccg") {
    prod.cards = p.cards ?? [];
    prod.setPower = fillSet(prod, prod.q.design, r);
    runMeta(state, co, lineRoot(co, prod));
  }
  if (prod.kind === "rpg_supplement") {
    const root = lineRoot(co, prod);
    root.community = clamp((root.community ?? 0) + 0.06 + 0.12 * prod.q.writing);
    if (root.community > 0.5 && (root.community - 0.06 - 0.12 * prod.q.writing) <= 0.5) log(state, "community", `Fans of ${root.design.name} are writing their own adventures. The line has a community now.`, co.id);
  }
  if (p.kind === "license" && p.licence) {
    const L = C.licence[p.licence];
    const late = p.deadlineWeek !== undefined && state.week > p.deadlineWeek;
    const flopped = r.chance(L.flop);
    prod.licence = { id: L.id, flopped, until: state.week + (late ? 8 : 52) };
    if (late) co.reputation = clamp(co.reputation - 2, 0, 100);
    log(state, "licence", `${L.name} ${flopped ? "flops at the box office; the game has to stand on its own" : `is a hit, and ${prod.design.name} rides the wave`}${late ? ". You missed the deadline, so the studio's promotion is over quickly." : "."}`, co.id);
  }
  if (prod.kind === "spinoff") {
    const root = co.products.find((x) => x.id === p.parentId);
    if (root) {
      prod.hype = clamp(prod.hype + 0.15 + 0.25 * (root.score / 10));
      co.mods.push({ kind: "sales", value: 1.25, target: `product:${prod.id}`, until: state.week + 52, label: `${root.design.name} fans` });
    }
  }
}

/** Monthly meta runs, RPG communities bringing fans, licence offers coming and going. */
export function linesWeek(state: GameState, co: Company): void {
  if (state.week % 4 === 0) {
    for (const p of co.products) if (p.design.format === "ccg" && p.kind === "base" && p.launched && !p.retired) runMeta(state, co, p);
  }
  for (const p of co.products) if (p.community) co.fans += Math.round(p.community * 8);
  co.licenceOffers = (co.licenceOffers ?? []).filter((o) => o.expires > state.week);
  pitchesWeek(state, co);
  rivalMovesWeek(state, co);
  // a licence offer every few years once you're in an office (more with the licensing department)
  const every = Math.round(3 * WEEKS_PER_YEAR / (1 + perkFx(co, "licences")));
  if (co.level !== "garage" && state.week % every === (co.id.length * 7) % every) {
    const r = rng(state, `lines:${co.id}`);
    const used = new Set(co.products.map((p) => p.licence?.id).filter(Boolean));
    const avail = licences.filter((l) => l.year <= yearOf(state.week) && !used.has(l.id) && !co.licenceOffers.some((o) => o.id === l.id));
    if (avail.length) offerLicence(state, co, r.pick(avail).id);
  }
}

export function offerLicence(state: GameState, co: Company, id: string): void {
  const L = C.licence[id];
  if (!L || (co.licenceOffers ?? []).some((o) => o.id === id)) return;
  co.licenceOffers = [...(co.licenceOffers ?? []), { id, expires: state.week + 26 }];
  log(state, "licence", `Licence offer: make a ${C.theme[L.theme]?.name.toLowerCase() ?? ""} game of ${L.name}. Fee ${money(L.fee * priceIndex(state.week))}, ${L.deadlineWeeks} weeks to deliver.`, co.id);
}
